/**
 * About Timeline (horizontal split-panel filmstrip)
 * Ported 1:1 from compound-html src/entrypoints/about.js — initAboutTimeline
 *
 * Desktop (>=992px): pinned section; horizontal track translates on x.
 * Mobile (<992px): panels stack; in-view reveal on scroll.
 *
 * @param {HTMLElement} section — root .about-timeline element
 * @param {object|undefined} lenis — optional Lenis instance (skip uses lenis.scrollTo if available)
 */
function initAboutTimelineFilmstrip(section, lenis) {
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  if (!section || !gsap || !ScrollTrigger) return;

  if (section._aboutTimelineFilmstripMm) {
    section._aboutTimelineFilmstripMm.revert();
    section._aboutTimelineFilmstripMm = null;
  }

  var viewport = section.querySelector('.about-timeline__viewport');
  var track = section.querySelector('.about-timeline__track');
  var panels = Array.from(section.querySelectorAll('.tl-panel'));
  var skipBtn = section.querySelector('[data-timeline-skip]');
  if (!viewport || !track || !panels.length) return;

  var refreshOnImagesLoaded = function () {
    var imgs = Array.from(section.querySelectorAll('img'));
    if (!imgs.length) return;
    var remaining = 0;
    imgs.forEach(function (img) {
      if (img.complete && img.naturalWidth) return;
      remaining += 1;
      var done = function () {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        remaining -= 1;
        if (remaining === 0) ScrollTrigger.refresh();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  };
  refreshOnImagesLoaded();

  if (document.readyState === 'complete') {
    ScrollTrigger.refresh();
  } else {
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    }, { once: true });
  }

  if (skipBtn) {
    skipBtn.addEventListener('click', function () {
      var next = section.nextElementSibling;
      if (!next) return;
      if (lenis && typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(next, { offset: 0, duration: 1.2 });
      } else {
        next.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  var mm = gsap.matchMedia();
  section._aboutTimelineFilmstripMm = mm;

  var elTrigger = function (el, containerAnim, opts) {
    opts = opts || {};
    var hStart = opts.hStart != null ? opts.hStart : 'left 88%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 88%';
    var toggleActions = opts.toggleActions || 'play none none reverse';
    if (!containerAnim) {
      return { trigger: el, start: vStart, toggleActions: toggleActions };
    }
    return { trigger: el, containerAnimation: containerAnim, start: hStart, toggleActions: toggleActions };
  };

  var setupPolaroidParallax = function (panel, depthConfig, opts) {
    opts = opts || {};
    var base = opts.base != null ? opts.base : 14;
    var step = opts.step != null ? opts.step : 6;
    var scrub = opts.scrub != null ? opts.scrub : 0.8;
    if (panel.classList.contains('tl-panel--d')) {
      var strip = panel.querySelector('.tl-panel__polaroids');
      if (!strip) return;
      gsap.fromTo(strip, { x: -20 }, {
        x: 20, ease: 'none', immediateRender: false,
        scrollTrigger: Object.assign({}, depthConfig, { invalidateOnRefresh: true, scrub: scrub })
      });
      return;
    }
    Array.from(panel.querySelectorAll('.polaroid')).forEach(function (el, idx) {
      var amp = base + (idx * step) + (idx < 2 ? 4 : 0);
      var dir = idx % 2 === 0 ? -1 : 1;
      if (panel.classList.contains('tl-panel--b') && el.classList.contains('polaroid--p2')) amp *= 0.4;
      var override = parseFloat(el.dataset.parallaxAmp);
      if (!Number.isNaN(override)) amp = override;
      gsap.fromTo(el, { x: -amp * dir }, {
        x: amp * dir, ease: 'none', immediateRender: false,
        scrollTrigger: Object.assign({}, depthConfig, { invalidateOnRefresh: true, scrub: scrub })
      });
    });
  };

  var fadeIn = function (el, containerAnim, opts) {
    opts = opts || {};
    var duration = opts.duration != null ? opts.duration : 0.5;
    gsap.set(el, { autoAlpha: 0 });
    gsap.to(el, {
      autoAlpha: 1, duration: duration, ease: 'power2.out',
      scrollTrigger: elTrigger(el, containerAnim, { hStart: opts.hStart, vStart: opts.vStart })
    });
  };

  var setupFigureImgFade = function (figures, containerAnim, opts) {
    opts = opts || {};
    var duration = opts.duration != null ? opts.duration : 0.5;
    var hStart = opts.hStart != null ? opts.hStart : 'left 88%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 88%';
    Array.from(figures).forEach(function (fig) {
      var img = fig.querySelector('img');
      if (!img) return;
      gsap.set(img, { autoAlpha: 0 });
      gsap.to(img, {
        autoAlpha: 1, duration: duration, ease: 'power2.out',
        scrollTrigger: elTrigger(fig, containerAnim, { hStart: hStart, vStart: vStart })
      });
    });
  };

  var setupElementDepth = function (figures, containerAnim, depthConfig, opts) {
    opts = opts || {};
    var base = opts.base != null ? opts.base : 18;
    var step = opts.step != null ? opts.step : 6;
    var scrub = opts.scrub != null ? opts.scrub : 0.85;
    var fadeDuration = opts.fadeDuration != null ? opts.fadeDuration : 0.5;
    var hStart = opts.hStart != null ? opts.hStart : 'left 88%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 88%';
    Array.from(figures).forEach(function (fig, idx) {
      var img = fig.querySelector('img');
      if (img) {
        gsap.set(img, { autoAlpha: 0 });
        gsap.to(img, {
          autoAlpha: 1, duration: fadeDuration, ease: 'power2.out',
          scrollTrigger: elTrigger(fig, containerAnim, { hStart: hStart, vStart: vStart })
        });
      }
      var amp = base + (idx * step);
      var dir = idx % 2 === 0 ? -1 : 1;
      gsap.fromTo(fig, { x: -amp * dir }, {
        x: amp * dir, ease: 'none', immediateRender: false,
        scrollTrigger: Object.assign({}, depthConfig, { invalidateOnRefresh: true, scrub: scrub })
      });
    });
  };

  var setupReelsDepth = function (panel, containerAnim, depthConfig, opts) {
    opts = opts || {};
    var amp = opts.amp != null ? opts.amp : 22;
    var scrub = opts.scrub != null ? opts.scrub : 0.85;
    var fadeDuration = opts.fadeDuration != null ? opts.fadeDuration : 0.5;
    var direction = opts.direction != null ? opts.direction : 1;
    var hStart = opts.hStart != null ? opts.hStart : 'left 88%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 88%';
    var reels = panel.querySelector('.tl-panel__reels');
    if (!reels) return;
    gsap.set(reels, { autoAlpha: 0, '--reels-parallax-x': '0px' });
    gsap.to(reels, {
      autoAlpha: 1, duration: fadeDuration, ease: 'power2.out',
      scrollTrigger: elTrigger(reels, containerAnim, { hStart: hStart, vStart: vStart })
    });
    gsap.fromTo(reels, { '--reels-parallax-x': (-amp * direction) + 'px' }, {
      '--reels-parallax-x': (amp * direction) + 'px',
      ease: 'none', immediateRender: false,
      scrollTrigger: Object.assign({}, depthConfig, { invalidateOnRefresh: true, scrub: scrub })
    });
  };

  var setupBadgeReveal = function (panel, containerAnim, opts) {
    opts = opts || {};
    var duration = opts.duration != null ? opts.duration : 0.38;
    var stagger = opts.stagger != null ? opts.stagger : 0.1;
    var y = opts.y != null ? opts.y : 18;
    var scale = opts.scale != null ? opts.scale : 0.96;
    var hStart = opts.hStart != null ? opts.hStart : 'left 85%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 82%';
    var badges = Array.from(panel.querySelectorAll('.tl-badge'));
    if (!badges.length) return;
    var anchor = panel.querySelector('.tl-panel__badges') || badges[0];
    gsap.set(badges, { autoAlpha: 0, y: y, scale: scale });
    gsap.to(badges, {
      autoAlpha: 1, y: 0, scale: 1, duration: duration, ease: 'power2.out', stagger: stagger,
      scrollTrigger: elTrigger(anchor, containerAnim, { hStart: hStart, vStart: vStart })
    });
  };

  var setupSparkleDraw = function (panel, containerAnim, opts) {
    opts = opts || {};
    var duration = opts.duration != null ? opts.duration : 0.6;
    var stagger = opts.stagger != null ? opts.stagger : 0.06;
    var hStart = opts.hStart != null ? opts.hStart : 'left 85%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 85%';
    Array.from(panel.querySelectorAll('.tl-sparkle')).forEach(function (sparkle) {
      var paths = Array.from(sparkle.querySelectorAll('path'));
      if (!paths.length) return;
      gsap.set(sparkle, { autoAlpha: 0 });
      paths.forEach(function (p) {
        var len = 0;
        try { len = p.getTotalLength(); } catch (e) { /* element not yet laid out */ }
        if (!len) return;
        gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
      });
      gsap.timeline({ scrollTrigger: elTrigger(sparkle, containerAnim, { hStart: hStart, vStart: vStart }) })
        .to(sparkle, { autoAlpha: 1, duration: 0.2, ease: 'power1.out' })
        .to(paths, { strokeDashoffset: 0, duration: duration, ease: 'power2.out', stagger: stagger }, '<');
    });
  };

  var setupArrowDraw = function (panel, containerAnim, opts) {
    opts = opts || {};
    var duration = opts.duration != null ? opts.duration : 1.6;
    var stagger = opts.stagger != null ? opts.stagger : 0.18;
    var fadeDuration = opts.fadeDuration != null ? opts.fadeDuration : 0.3;
    var hStart = opts.hStart != null ? opts.hStart : 'left 80%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 80%';
    Array.from(panel.querySelectorAll('.tl-arrow')).forEach(function (arrow) {
      var paths = Array.from(arrow.querySelectorAll('path'));
      if (!paths.length) return;
      gsap.set(arrow, { autoAlpha: 0 });
      paths.forEach(function (p) {
        var len = 0;
        try { len = p.getTotalLength(); } catch (e) { /* element not yet laid out */ }
        if (!len) return;
        gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
      });
      gsap.timeline({ scrollTrigger: elTrigger(arrow, containerAnim, { hStart: hStart, vStart: vStart }) })
        .to(arrow, { autoAlpha: 1, duration: fadeDuration, ease: 'power1.out' })
        .to(paths, { strokeDashoffset: 0, duration: duration, ease: 'power2.out', stagger: stagger }, '<0.05');
    });
  };

  var setupMediaFade = function (panel, containerAnim, opts) {
    opts = opts || {};
    var fadeDuration = opts.fadeDuration != null ? opts.fadeDuration : 0.5;
    var hStart = opts.hStart != null ? opts.hStart : 'left 90%';
    var vStart = opts.vStart != null ? opts.vStart : 'top 88%';
    var media = panel.querySelector('.tl-panel__media');
    if (!media) return;
    fadeIn(media, containerAnim, { duration: fadeDuration, hStart: hStart, vStart: vStart });
  };

  var setupTextReveal = function (panel, containerAnim, opts) {
    opts = opts || {};
    var includeBadgesAfter = !!opts.includeBadgesAfter;
    var yearEl = panel.querySelector('.tl-panel__year[data-animation="line-reveal"]');
    var yearLines = Array.from(panel.querySelectorAll('.tl-panel__year[data-animation="line-reveal"] .elm'));
    var titleLines = Array.from(panel.querySelectorAll('.tl-panel__title[data-animation="line-reveal"] .elm'));
    var titleFallback = Array.from(panel.querySelectorAll('.tl-panel__title[data-animation="line-reveal"]:not(.text-line-reveal)'));
    var descEls = Array.from(panel.querySelectorAll('.tl-panel__desc[data-animation="opacity"]'));
    var badgeEls = includeBadgesAfter ? Array.from(panel.querySelectorAll('.tl-badge')) : [];
    var hasAny = yearEl || yearLines.length || titleLines.length || descEls.length;
    if (!hasAny) return;
    var anchor = yearEl
      || panel.querySelector('.tl-panel__title[data-animation="line-reveal"] .elm')
      || panel.querySelector('.tl-panel__desc[data-animation="opacity"]');
    if (!anchor) return;

    // Year pill: GSAP opacity + border draw (not a line reveal).
    if (yearEl) gsap.set(yearEl, { autoAlpha: 0, borderColor: 'transparent' });
    // Desc / badges / fallback title: opacity only.
    if (descEls.length) gsap.set(descEls, { autoAlpha: 0 });
    if (badgeEls.length) gsap.set(badgeEls, { autoAlpha: 0, y: 18, scale: 0.96 });
    if (titleFallback.length) gsap.set(titleFallback, { autoAlpha: 0, y: 24 });

    // Year lines + title lines: DO NOT set GSAP transforms here.
    // Global CSS already clips each span at translateY(105%) behind the .elm's overflow:hidden.
    // We replicate the founders-section / global IntersectionObserver behaviour by adding
    // 'in-view' to each .elm via a GSAP call() — the CSS transition then does the reveal,
    // line by line, identical to every other page on the site.

    var tl = gsap.timeline({
      scrollTrigger: elTrigger(anchor, containerAnim, { hStart: 'left 88%', vStart: 'top 88%' })
    });

    if (yearEl) {
      tl.to(yearEl, { autoAlpha: 1, duration: 0.3, ease: 'power1.out', borderColor: 'currentColor' });
    }

    // Year line-reveals (staggered calls)
    var yearCallBase = yearEl ? 0.04 : 0;
    yearLines.forEach(function (el, i) {
      tl.call(function () { el.classList.add('in-view'); }, null, yearCallBase + i * 0.05);
    });

    // Title line-reveals (staggered calls, start after year finishes)
    var titleCallBase = (yearEl ? 0.3 : 0) + yearLines.length * 0.05 + 0.04;
    if (titleLines.length) {
      titleLines.forEach(function (el, i) {
        tl.call(function () { el.classList.add('in-view'); }, null, titleCallBase + i * 0.07);
      });
    } else if (titleFallback.length) {
      tl.to(titleFallback, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' }, titleCallBase);
    }

    var afterTitle = titleCallBase + (titleLines.length ? titleLines.length * 0.07 : 0.5);
    if (descEls.length) {
      tl.to(descEls, { autoAlpha: 1, duration: 0.25, ease: 'power1.out', stagger: 0.04 }, afterTitle + 0.12);
    }
    if (badgeEls.length) {
      tl.to(badgeEls, { autoAlpha: 1, y: 0, scale: 1, duration: 0.38, ease: 'back.out(1.4)', stagger: 0.1 },
        afterTitle + (descEls.length ? descEls.length * 0.04 + 0.3 : 0.18));
    }
  };

  var clearAllProps = function () {
    panels.forEach(function (panel) {
      gsap.set(panel.querySelectorAll('[data-reveal]'), { clearProps: 'opacity,transform,visibility' });
      gsap.set(panel.querySelectorAll('.tl-panel__year,.tl-panel__year .elm,.tl-panel__title .elm,.tl-panel__desc'), {
        clearProps: 'opacity,transform,visibility'
      });
      // Remove 'in-view' from .elm elements added by setupTextReveal's call() so the
      // CSS initial state (span at translateY(105%) behind overflow:hidden) is restored
      // before GSAP re-initialises for a different breakpoint.
      panel.querySelectorAll('.tl-panel__year .elm,.tl-panel__title .elm').forEach(function (el) {
        el.classList.remove('in-view');
      });
      gsap.set(panel.querySelectorAll('.tl-panel__media,.polaroid img,.tl-panel__certificate img,.product-shot img,.reel img'), {
        clearProps: 'opacity,transform,visibility'
      });
      gsap.set(panel.querySelectorAll('.tl-panel__polaroids,.tl-badge'), {
        clearProps: 'opacity,transform,visibility'
      });
      gsap.set(panel.querySelectorAll('.tl-sparkle,.tl-sparkle path,.tl-arrow,.tl-arrow path'), {
        clearProps: 'opacity,visibility,strokeDasharray,strokeDashoffset,transform'
      });
      gsap.set(panel.querySelectorAll('.tl-panel__reels'), { clearProps: 'opacity,visibility,--reels-parallax-x' });
    });
  };

  mm.add('(min-width: 992px)', function () {
    var getScrollDistance = function () {
      return track.scrollWidth - window.innerWidth;
    };
    var containerAnim = gsap.to(track, {
      x: function () { return -getScrollDistance(); },
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: function () { return '+=' + getScrollDistance(); },
        scrub: 1,
        pin: viewport,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });
    panels.forEach(function (panel, i) {
      var ca = i === 0 ? null : containerAnim;
      var depthConfig = i === 0
        ? { trigger: section, start: 'top bottom', end: 'bottom top' }
        : { trigger: panel, containerAnimation: containerAnim, start: 'left right', end: 'right left' };
      setupMediaFade(panel, ca);
      setupPolaroidParallax(panel, depthConfig, { base: 14, step: 6, scrub: 0.85 });
      setupFigureImgFade(panel.querySelectorAll('.polaroid'), ca);
      setupFigureImgFade(panel.querySelectorAll('.reel'), ca, { duration: 0.45 });
      setupElementDepth(panel.querySelectorAll('.tl-panel__certificate'), ca, depthConfig, { base: 16, step: 0, scrub: 0.8 });
      setupElementDepth(panel.querySelectorAll('.product-shot'), ca, depthConfig, { base: 18, step: 8, scrub: 0.85 });
      setupReelsDepth(panel, ca, depthConfig, { amp: 22, scrub: 0.8, direction: -1 });
      var isPanelD = panel.classList.contains('tl-panel--d');
      if (!isPanelD) setupBadgeReveal(panel, ca);
      setupSparkleDraw(panel, ca);
      setupArrowDraw(panel, ca);
      setupTextReveal(panel, ca, { includeBadgesAfter: isPanelD });
    });
    return function () {
      gsap.set(track, { clearProps: 'transform' });
      clearAllProps();
    };
  });

  mm.add('(max-width: 991px)', function () {
    panels.forEach(function (panel) {
      var depthConfig = { trigger: panel, start: 'top 90%', end: 'bottom 10%' };
      setupMediaFade(panel, null, { fadeDuration: 0.4, vStart: 'top 85%' });
      setupPolaroidParallax(panel, depthConfig, { base: 6, step: 3, scrub: 0.65 });
      setupFigureImgFade(panel.querySelectorAll('.polaroid'), null, { vStart: 'top 85%' });
      setupFigureImgFade(panel.querySelectorAll('.reel'), null, { duration: 0.45, vStart: 'top 85%' });
      setupElementDepth(panel.querySelectorAll('.tl-panel__certificate'), null, depthConfig, { base: 10, step: 0, scrub: 0.6, vStart: 'top 85%' });
      setupElementDepth(panel.querySelectorAll('.product-shot'), null, depthConfig, { base: 10, step: 4, scrub: 0.65, vStart: 'top 85%' });
      setupReelsDepth(panel, null, depthConfig, { amp: 10, scrub: 0.6, direction: -1, vStart: 'top 85%' });
      var isPanelD = panel.classList.contains('tl-panel--d');
      if (!isPanelD) setupBadgeReveal(panel, null, { vStart: 'top 82%' });
      setupSparkleDraw(panel, null, { vStart: 'top 85%' });
      setupArrowDraw(panel, null, { vStart: 'top 80%' });
      setupTextReveal(panel, null, { includeBadgesAfter: isPanelD });
    });
    return function () { clearAllProps(); };
  });

  setTimeout(function () { ScrollTrigger.refresh(); }, 600);
}

window.initAboutTimelineFilmstrip = initAboutTimelineFilmstrip;

window.destroyAboutTimelineFilmstrip = function (section) {
  if (section && section._aboutTimelineFilmstripMm) {
    section._aboutTimelineFilmstripMm.revert();
    section._aboutTimelineFilmstripMm = null;
  }
};
